import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Loader2, 
  AlertCircle, CheckCircle2, Activity, Dumbbell, Clock,
  BarChart3, Flame, Target, ArrowUp, ArrowDown
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_STYLES = {
  danger: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/50',
    text: 'text-red-400',
    icon: AlertCircle
  },
  warning: {
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/50',
    text: 'text-yellow-400',
    icon: AlertTriangle
  },
  success: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/50',
    text: 'text-green-400',
    icon: CheckCircle2
  }
};

const MOOD_LABELS = {
  excellent: { emoji: '😀', label: 'Excelente', color: 'text-green-400' },
  good: { emoji: '😊', label: 'Bien', color: 'text-emerald-400' },
  neutral: { emoji: '😐', label: 'Normal', color: 'text-yellow-400' },
  tired: { emoji: '😓', label: 'Cansado', color: 'text-orange-400' },
  exhausted: { emoji: '😫', label: 'Agotado', color: 'text-red-400' }
};

const ClientMonitoringPanel = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMonitoringData();
  }, [clientId]);

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/client/${clientId}/monitoring`);
      setData(response.data);
    } catch (err) {
      setError('Error al cargar datos de monitoreo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-500/10 border-red-500/30">
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const { alerts, alerts_count, quick_stats, exercise_charts, exercises_detail } = data;

  return (
    <div className="space-y-6">
      {/* ========== ALERTAS ========== */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              ALERTAS Y NOTIFICACIONES
            </span>
            <div className="flex items-center gap-2 text-sm">
              {alerts_count.danger > 0 && (
                <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                  {alerts_count.danger} urgente{alerts_count.danger > 1 ? 's' : ''}
                </span>
              )}
              {alerts_count.warning > 0 && (
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-bold">
                  {alerts_count.warning} aviso{alerts_count.warning > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {alerts.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-2" />
              <p className="text-green-400 font-semibold">Todo en orden</p>
              <p className="text-sm text-muted-foreground">No hay alertas para este cliente</p>
            </div>
          ) : (
            alerts.map((alert, idx) => {
              const style = ALERT_STYLES[alert.type] || ALERT_STYLES.warning;
              const Icon = style.icon;
              return (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-black/20">
                      <Icon className={`h-5 w-5 ${style.text}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{alert.icon}</span>
                        <h4 className={`font-bold ${style.text}`}>{alert.title}</h4>
                      </div>
                      <p className="text-sm text-foreground/80 mt-1">{alert.message}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ========== RESUMEN RÁPIDO ========== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <Dumbbell className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{quick_stats.total_sessions}</p>
            <p className="text-xs text-muted-foreground">Sesiones</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">S{quick_stats.current_week}</p>
            <p className="text-xs text-muted-foreground">Semana actual</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <span className="text-3xl mb-1 block">
              {MOOD_LABELS[quick_stats.mood_status]?.emoji || '😐'}
            </span>
            <p className={`text-sm font-semibold ${MOOD_LABELS[quick_stats.mood_status]?.color || 'text-foreground'}`}>
              {MOOD_LABELS[quick_stats.mood_status]?.label || 'Normal'}
            </p>
            <p className="text-xs text-muted-foreground">Estado promedio</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              <span className="text-lg font-bold text-green-400">{quick_stats.exercises_improving}</span>
              <TrendingDown className="h-5 w-5 text-red-400 ml-2" />
              <span className="text-lg font-bold text-red-400">{quick_stats.exercises_declining}</span>
            </div>
            <p className="text-xs text-muted-foreground">Mejoran / Bajan</p>
          </CardContent>
        </Card>
      </div>

      {/* ========== GRÁFICAS DE PROGRESIÓN ========== */}
      {exercise_charts && exercise_charts.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-cyan-400" />
              PROGRESIÓN DE CARGAS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {exercise_charts.map((chart, idx) => (
                <div key={idx} className="bg-black/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-foreground text-sm truncate flex-1">
                      {chart.name}
                    </h4>
                    <span className={`text-sm font-bold px-2 py-1 rounded ${
                      chart.trend === 'up' ? 'bg-green-500/20 text-green-400' :
                      chart.trend === 'down' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {chart.trend === 'up' && <ArrowUp className="h-3 w-3 inline mr-1" />}
                      {chart.trend === 'down' && <ArrowDown className="h-3 w-3 inline mr-1" />}
                      {chart.progression > 0 ? '+' : ''}{chart.progression}%
                    </span>
                  </div>
                  
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chart.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis 
                          dataKey="week" 
                          tick={{ fill: '#888', fontSize: 10 }}
                          tickFormatter={(w) => `S${w}`}
                        />
                        <YAxis 
                          tick={{ fill: '#888', fontSize: 10 }}
                          domain={['auto', 'auto']}
                          tickFormatter={(v) => `${v}kg`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a1a', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value} kg`, 'Carga']}
                          labelFormatter={(w) => `Semana ${w}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="load" 
                          stroke={chart.trend === 'up' ? '#22c55e' : chart.trend === 'down' ? '#ef4444' : '#eab308'}
                          strokeWidth={2}
                          dot={{ fill: '#d4a84b', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>Inicio: {chart.first_load}kg</span>
                    <span>Actual: {chart.last_load}kg</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== DETALLE DE EJERCICIOS ========== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Mejorando */}
        {exercises_detail?.improving?.length > 0 && (
          <Card className="bg-green-500/5 border-green-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-green-400 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                MEJORANDO ({exercises_detail.improving.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {exercises_detail.improving.map((ex, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-green-500/10 rounded p-2">
                  <span className="text-foreground truncate flex-1">{ex.name}</span>
                  <span className="text-green-400 font-bold ml-2">+{ex.change}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Estancados */}
        {exercises_detail?.stagnant?.length > 0 && (
          <Card className="bg-yellow-500/5 border-yellow-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                <Minus className="h-4 w-4" />
                ESTANCADOS ({exercises_detail.stagnant.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {exercises_detail.stagnant.slice(0, 5).map((ex, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-yellow-500/10 rounded p-2">
                  <span className="text-foreground truncate flex-1">{ex.name}</span>
                  <span className="text-yellow-400 font-bold ml-2">{ex.load}kg</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Bajando */}
        {exercises_detail?.declining?.length > 0 && (
          <Card className="bg-red-500/5 border-red-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-red-400 flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                BAJANDO ({exercises_detail.declining.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {exercises_detail.declining.map((ex, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-red-500/10 rounded p-2">
                  <span className="text-foreground truncate flex-1">{ex.name}</span>
                  <span className="text-red-400 font-bold ml-2">{ex.change}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sin datos */}
      {(!exercise_charts || exercise_charts.length === 0) && (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              Aún no hay suficientes datos para mostrar gráficas de progresión.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Se necesitan al menos 2 sesiones con el mismo ejercicio.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientMonitoringPanel;
